/* ============================================================
   Urban Junglist Co. - Pre-launch countdown

   Deliberately a standalone module rather than part of
   ujc-prelaunch.js. The signup enhancement and the entrance must not
   be able to fail because of a countdown error, and the countdown must
   run whether or not GSAP loaded. Nothing here touches the form.

   Without JavaScript the section renders a static launch line instead,
   so this script only ever upgrades what is already readable.
   ============================================================ */

(function () {
  'use strict';

  var registry = new WeakMap();

  function pad(value) {
    return value < 10 ? '0' + value : String(value);
  }

  function Countdown(el) {
    this.el = el;
    this.interval = null;
    this.announcedMinute = null;

    var raw = el.getAttribute('data-launch-date');
    this.target = raw ? Date.parse(raw) : NaN;
    this.completeText = el.getAttribute('data-complete-text') || '';

    this.staticLine = el.querySelector('.ujc-countdown__static');
    this.timer = el.querySelector('.ujc-countdown__timer');
    this.status = el.querySelector('.ujc-countdown__status');

    this.values = {
      days: el.querySelector('[data-unit="days"]'),
      hours: el.querySelector('[data-unit="hours"]'),
      minutes: el.querySelector('[data-unit="minutes"]'),
      seconds: el.querySelector('[data-unit="seconds"]')
    };

    /* Missing, malformed or already-past dates: leave the server-rendered
       line exactly as it is. Silence is the correct failure mode here. */
    if (!this.timer || isNaN(this.target)) return;

    this.onVisibility = this.handleVisibility.bind(this);
    document.addEventListener('visibilitychange', this.onVisibility);

    this.start();
  }

  Countdown.prototype.start = function () {
    if (Date.now() >= this.target) {
      this.complete();
      return;
    }

    if (this.staticLine) this.staticLine.hidden = true;
    this.timer.hidden = false;

    this.tick();
    this.resume();
  };

  Countdown.prototype.resume = function () {
    if (this.interval) return;
    var self = this;
    this.interval = window.setInterval(function () { self.tick(); }, 1000);
  };

  Countdown.prototype.stop = function () {
    if (!this.interval) return;
    window.clearInterval(this.interval);
    this.interval = null;
  };

  /* No timer runs in a background tab. On return, tick once immediately
     so the display is correct before the next interval fires. */
  Countdown.prototype.handleVisibility = function () {
    if (document.hidden) {
      this.stop();
      return;
    }
    this.tick();
    this.resume();
  };

  Countdown.prototype.tick = function () {
    var remaining = this.target - Date.now();

    if (remaining <= 0) {
      this.complete();
      return;
    }

    var totalSeconds = Math.floor(remaining / 1000);
    var days = Math.floor(totalSeconds / 86400);
    var hours = Math.floor((totalSeconds % 86400) / 3600);
    var minutes = Math.floor((totalSeconds % 3600) / 60);
    var seconds = totalSeconds % 60;

    if (this.values.days) this.values.days.textContent = pad(days);
    if (this.values.hours) this.values.hours.textContent = pad(hours);
    if (this.values.minutes) this.values.minutes.textContent = pad(minutes);
    if (this.values.seconds) this.values.seconds.textContent = pad(seconds);

    /* The visible digits are aria-hidden. Announcing every second would
       make the page unusable with a screen reader, so the live region is
       refreshed only when the minute changes, and it omits seconds. */
    if (this.status && minutes !== this.announcedMinute) {
      this.announcedMinute = minutes;
      this.status.textContent =
        days + ' days, ' + hours + ' hours and ' + minutes + ' minutes until launch.';
    }
  };

  Countdown.prototype.complete = function () {
    this.stop();

    if (this.timer) this.timer.hidden = true;

    if (this.staticLine) {
      this.staticLine.textContent = this.completeText;
      this.staticLine.classList.add('ujc-countdown__static--complete');
      this.staticLine.hidden = false;
    }

    if (this.status) this.status.textContent = this.completeText;
  };

  Countdown.prototype.destroy = function () {
    this.stop();
    if (this.onVisibility) {
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.onVisibility = null;
    }
  };

  function mount(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll('.ujc-countdown');

    Array.prototype.forEach.call(nodes, function (el) {
      if (registry.has(el)) return;
      registry.set(el, new Countdown(el));
    });
  }

  function unmount(el) {
    var instance = registry.get(el);
    if (!instance) return;
    instance.destroy();
    registry.delete(el);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { mount(); });
  } else {
    mount();
  }

  document.addEventListener('shopify:section:load', function (event) {
    mount(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var stale = event.target.querySelectorAll('.ujc-countdown');
    Array.prototype.forEach.call(stale, unmount);
  });
})();
